import { api } from '../api/client';
import { API_AUTOMATIONS_URL, API_AUTOMATION_URL, API_AUTOMATION_RUNS_URL } from '../utils';

/**
 * Read side of the automations subsystem: the rules a collection runs, and one
 * rule's run ledger. Both are manage-gated by the API — a read/write sharee
 * gets a 403 and an outsider a 404, so nothing here needs its own permission
 * check.
 *
 * Driven fields are authored on the target ELEMENT (options.drivenFrom in the
 * card definition form) and materialized into managed rules server-side on card
 * save; this module never writes them — the create/patch/delete trio below is
 * for user-authored `kind: 'rule'` rows only, and the API answers a 400 naming
 * the owning card/element if one is ever aimed at a managed rule.
 *
 * The one write this module does carry is the enable/disable pair: pausing a
 * managed rule is a runtime decision (a source card is being reorganized, a
 * recompute is misbehaving), not a re-authoring of the element config, so it
 * belongs on the rule and not on the card definition.
 */
export async function getAutomations( collectionId, { includeInherited = false, describe = false } = {} ) {
	// `includeInherited`: a sub-collection's list also carries the rules of
	// the collections above it that run over it (keepr-api § Sub-collections,
	// family-wide definitions) — appended, each `inherited: true` with
	// `inheritedFrom`, and read-only here.
	// `describe` (KPR-196, keepr-api KPR-194): each rule also carries keepr's
	// plain sentence for what it does (`summary`, null for a managed one) and
	// how it arrives (`arrives`).
	const params = {
		...( includeInherited ? { includeInherited: 1 } : {} ),
		...( describe ? { describe: 1 } : {} ),
	};
	const { data } = Object.keys( params ).length
		? await api.get( API_AUTOMATIONS_URL( collectionId ), { params } )
		: await api.get( API_AUTOMATIONS_URL( collectionId ) );
	return data || [];
}

/**
 * Newest-first run history for one rule. `limit`/`skip` are the API's own
 * pagination params; omitted keys simply take the server default.
 */
export async function getAutomationRuns( collectionId, automationId, { limit, skip } = {} ) {
	const params = {};
	if ( limit !== undefined ) params.limit = limit;
	if ( skip !== undefined ) params.skip = skip;
	const { data } = await api.get( API_AUTOMATION_RUNS_URL( collectionId, automationId ), { params } );
	return data || [];
}

/**
 * Enable / disable one rule. Both endpoints are idempotent on the API side
 * (they set `enabled` rather than toggling it), so an optimistic UI switch can
 * retry or double-fire without landing in the opposite state from the one the
 * user clicked.
 */
export function enableAutomation( collectionId, automationId ) {
	return api.post( `${API_AUTOMATION_URL( collectionId, automationId )}/enable` );
}

export function disableAutomation( collectionId, automationId ) {
	return api.post( `${API_AUTOMATION_URL( collectionId, automationId )}/disable` );
}

/**
 * Authoring writes for event rules. Every one of these can come back a 400 with
 * a field-level, human-written message ("trigger.elementsChanged must name
 * elements that exist on the trigger card."): the API validates a rule's whole
 * shape at write time precisely so a manager sees the problem at authoring
 * rather than at 3am. Callers surface `error.response.data.message` VERBATIM —
 * paraphrasing it would throw away the only part that says which field is wrong.
 */
export async function createAutomation( collectionId, payload ) {
	const { data } = await api.post( API_AUTOMATIONS_URL( collectionId ), payload );
	return data;
}

/**
 * Partial update. `trigger` and `actions` re-validate as a PAIR server-side (a
 * new trigger card can invalidate existing actions), so the editor always sends
 * both together rather than trying to patch one half.
 *
 * `merge: false` in the payload says the supplied sub-objects REPLACE the
 * stored ones. Every editor in the app sends it, because each of them holds a
 * rule's whole state: a key it leaves out is a setting the manager cleared, not
 * one they left alone, and only replacement spells that.
 */
export async function patchAutomation( collectionId, automationId, payload ) {
	const { data } = await api.patch( API_AUTOMATION_URL( collectionId, automationId ), payload );
	return data;
}

/** Soft delete — the row is flagged, not dropped, and drops out of the list. */
export function deleteAutomation( collectionId, automationId ) {
	return api.delete( API_AUTOMATION_URL( collectionId, automationId ) );
}

/**
 * Render a notify action against REAL data without sending or ledgering
 * anything. Called with the editor's CURRENT UNSAVED draft — that is the whole
 * point: a manager sees what the notification will actually say before the rule
 * exists, and the endpoint runs the same validation the save would, so its 400s
 * double as field-level authoring feedback (surfaced verbatim, like every other
 * write here).
 *
 * Payload: `{ kind, trigger?: { card_id }, check?, action }`.
 * Answer: `{ title, body, ctas, recipients, recipientCount, email, sample, warnings? }`.
 */
export async function previewNotify( collectionId, payload ) {
	const { data } = await api.post( `${ API_AUTOMATIONS_URL( collectionId ) }/notify-preview`, payload );
	return data;
}

/**
 * What a rule would do, written nowhere (keepr-api KPR-189): for a stored rule,
 * `{ automationId }` — its summary, today's matches for each write, the
 * rendered notification and who it reaches, its next runs and warnings. The
 * Turn on confirmation reads this before anything is switched (KPR-196).
 */
export async function previewAutomation( collectionId, payload ) {
	const { data } = await api.post( `${ API_AUTOMATIONS_URL( collectionId ) }/preview`, payload );
	return data;
}
